import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, ArrowLeft, Send, Loader2, Info, Package, Smile } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';

const BACKEND = RUNTIME_BACKEND_URL;

const QUICK_REPLIES = [
  { label: 'Tamam ✓', text: 'Tamam ✓' },
  { label: 'Teşekkürler 🙏', text: 'Teşekkürler 🙏' },
  { label: 'Ne zaman?', text: 'Ne zaman hazır olur? ⏰' },
  { label: 'Kargo no?', text: 'Kargo takip numarası var mı? 📦' },
];
const EMOJIS = ['👍','👏','❤️','🙏','🎉','🔥','✨','⏰','📦','✅','❌'];

/**
 * Floating Chat Widget — Dashboard sağ alt köşesinde
 * Fotoğrafçı ↔ Albümcü sipariş bazlı sohbet
 */
export default function FloatingChatWidget() {
  const { token, user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [unreadMap, setUnreadMap] = useState({}); // { orderId: count }
  const [totalUnread, setTotalUnread] = useState(0);
  const [activeOrder, setActiveOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const scrollRef = useRef(null);
  const lastNotifiedRef = useRef({}); // { orderId: lastMessageId } — son bildirim verdiğimiz mesaj

  const headers = { Authorization: `Bearer ${token}` };

  // Sadece fotoğrafçı ve albümcüler için göster
  const visible = user && (user.user_type === 'photographer' || user.user_type === 'albumcu' || !user.user_type);

  // Okunmamış sayısı + sipariş listesi yükle (genel polling)
  const loadAll = async () => {
    if (!token) return;
    try {
      const [uc, ord] = await Promise.all([
        fetch(`${BACKEND}/api/print-orders/messages/unread-count`, { headers }).then(r => r.ok ? r.json() : { total: 0, by_order: {} }),
        fetch(`${BACKEND}/api/print-orders`, { headers }).then(r => r.ok ? r.json() : [])
      ]);
      setUnreadMap(uc.by_order || {});
      setTotalUnread(uc.total || 0);
      setOrders(Array.isArray(ord) ? ord : []);

      // Yeni mesaj varsa toast bildirimi — sadece widget kapalıyken
      if (!open && uc.by_order) {
        const prevMap = lastNotifiedRef.current;
        for (const [oid, count] of Object.entries(uc.by_order)) {
          const prevCount = prevMap[oid] || 0;
          if (count > prevCount) {
            const ord2 = (ord || []).find(o => o.id === oid);
            if (ord2) {
              const otherName = user.user_type === 'albumcu' ? ord2.photographer_name : ord2.albumcu_name;
              toast.info(`💬 ${otherName}: yeni mesaj`, {
                description: `${ord2.product_name || ''} — ${ord2.customer_name || ''}`,
                action: { label: 'Aç', onClick: () => { setOpen(true); openConversation(ord2); } }
              });
            }
          }
        }
        lastNotifiedRef.current = { ...uc.by_order };
      }
    } catch {
      // sessizce geç
    }
  };

  // Aktif sipariş mesajlarını yükle
  const loadMessages = async (oid) => {
    if (!oid) return;
    try {
      const r = await fetch(`${BACKEND}/api/print-orders/${oid}/messages`, { headers });
      if (r.ok) {
        const data = await r.json();
        setMessages(Array.isArray(data) ? data : []);
      }
    } catch {}
  };

  const openConversation = async (o) => {
    setActiveOrder(o);
    setLoading(true);
    await loadMessages(o.id);
    setLoading(false);
    // Okunmuş sayılması için unread map'ten düşür
    setUnreadMap(prev => ({ ...prev, [o.id]: 0 }));
    setTotalUnread(prev => Math.max(0, prev - (unreadMap[o.id] || 0)));
  };

  const send = async (override) => {
    const t = (override !== undefined ? override : text).trim();
    if (!t || !activeOrder) return;
    setSending(true);
    try {
      const r = await fetch(`${BACKEND}/api/print-orders/${activeOrder.id}/messages`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: t })
      });
      if (r.ok) {
        if (override === undefined) setText('');
        await loadMessages(activeOrder.id);
      }
    } finally {
      setSending(false);
    }
  };

  // İlk yükleme + polling
  useEffect(() => {
    if (!visible) return;
    loadAll();
    const iv = setInterval(loadAll, 10000);
    return () => clearInterval(iv);
  }, [visible, token, open]);

  // Aktif sipariş açıkken daha hızlı mesaj poll
  useEffect(() => {
    if (!open || !activeOrder) return;
    const iv = setInterval(() => loadMessages(activeOrder.id), 5000);
    return () => clearInterval(iv);
  }, [open, activeOrder?.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  if (!visible) return null;

  // Conversation listesi: sadece sipariş varsa göster
  const conversations = orders.map(o => {
    const otherName = user.user_type === 'albumcu' ? o.photographer_name : o.albumcu_name;
    return {
      order: o,
      otherName: otherName || '-',
      unread: unreadMap[o.id] || 0,
      subtitle: `${o.product_name || ''} · ${o.customer_name || ''}`.replace(/^\s*·\s*/, '').trim(),
    };
  }).sort((a, b) => b.unread - a.unread || new Date(b.order.updated_at) - new Date(a.order.updated_at));

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="floating-chat-btn"
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-primary text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center"
          aria-label="Sohbet"
        >
          <MessageCircle className="h-6 w-6" />
          {totalUnread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 border-2 border-white">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-2rem)] bg-white rounded-2xl shadow-2xl border flex flex-col overflow-hidden"
          data-testid="floating-chat-panel">
          {/* Header */}
          <div className="bg-primary text-white p-3 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              {activeOrder ? (
                <>
                  <button onClick={() => setActiveOrder(null)} className="p-1 hover:bg-white/20 rounded">
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate">
                      {user.user_type === 'albumcu' ? activeOrder.photographer_name : activeOrder.albumcu_name}
                    </div>
                    <div className="text-[10px] text-white/80 truncate">{activeOrder.product_name}</div>
                  </div>
                </>
              ) : (
                <>
                  <MessageCircle className="h-5 w-5" />
                  <div>
                    <div className="font-bold text-sm">Sohbet</div>
                    <div className="text-[10px] text-white/80">
                      {totalUnread > 0 ? `${totalUnread} okunmamış mesaj` : 'Sipariş sohbetleri'}
                    </div>
                  </div>
                </>
              )}
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/20 rounded" data-testid="chat-close-btn">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          {!activeOrder ? (
            // Conversation list
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-xs">
                  <Package className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                  Henüz sipariş sohbeti yok<br />
                  <span className="text-[10px]">Albüm baskı siparişi oluşturduktan sonra sohbet başlayabilir</span>
                </div>
              ) : (
                conversations.map(c => (
                  <button
                    key={c.order.id}
                    onClick={() => openConversation(c.order)}
                    className="w-full text-left p-3 border-b hover:bg-slate-50 flex items-start gap-3"
                    data-testid={`chat-conv-${c.order.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      c.unread > 0 ? 'bg-primary/20 text-primary' : 'bg-slate-100 text-slate-500'
                    }`}>
                      <Package className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-sm truncate">{c.otherName}</span>
                        {c.unread > 0 && (
                          <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                            {c.unread}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">{c.subtitle}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : (
            <>
              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50/60">
                {loading ? (
                  <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-xs text-slate-400 py-4">İlk mesajı siz yazın</div>
                ) : messages.map(m => {
                  const isSystem = m.sender_role === 'system';
                  const isMine = m.sender_id === user?.id;
                  if (isSystem) {
                    return (
                      <div key={m.id} className="flex justify-center">
                        <div className="bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-[10px] text-amber-700 flex items-center gap-1 max-w-[90%]">
                          <Info className="h-2.5 w-2.5" />
                          <span className="truncate">{m.text}</span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[78%] rounded-2xl px-3 py-1.5 ${
                        isMine ? 'bg-primary text-white' : 'bg-white border border-slate-200 text-slate-800'
                      }`}>
                        <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                        <div className={`text-[9px] mt-0.5 ${isMine ? 'text-white/70' : 'text-slate-400'} text-right`}>
                          {new Date(m.at).toLocaleString('tr-TR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Quick replies - sadece son mesaj benim değilse göster */}
              {messages.length > 0 && messages[messages.length - 1]?.sender_id !== user?.id && (
                <div className="border-t bg-white px-2 py-1 flex gap-1 overflow-x-auto">
                  {QUICK_REPLIES.map((qr, i) => (
                    <button key={i} onClick={() => send(qr.text)}
                      className="flex-shrink-0 bg-slate-100 hover:bg-primary hover:text-white text-[11px] px-2 py-0.5 rounded-full transition whitespace-nowrap">
                      {qr.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Emoji picker */}
              {showEmoji && (
                <div className="border-t bg-white p-1.5 flex flex-wrap gap-1">
                  {EMOJIS.map((e, i) => (
                    <button key={i} onClick={() => { setText(t => t + e); setShowEmoji(false); }}
                      className="text-lg hover:bg-slate-100 rounded p-0.5 transition">
                      {e}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="border-t bg-white p-2 flex gap-1 items-end">
                <button onClick={() => setShowEmoji(s => !s)}
                  className="p-1.5 text-slate-400 hover:text-amber-500 rounded"
                  data-testid="widget-emoji-toggle">
                  <Smile className="h-5 w-5" />
                </button>
                <textarea
                  value={text}
                  onChange={e => setText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={1}
                  placeholder="Mesaj..."
                  className="flex-1 resize-none rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:border-primary max-h-20"
                  data-testid="floating-chat-input"
                />
                <button onClick={() => send()} disabled={sending || !text.trim()}
                  className="w-9 h-9 rounded-lg bg-primary text-white flex items-center justify-center disabled:opacity-50 hover:bg-primary/90"
                  data-testid="floating-chat-send">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
